import { ICareCategorySetting, IItemService } from "types/admin";
import DiagonalLine from "../../../../DiagonalLine";
import Decimal from "decimal.js";
import { DATE_ERA_FORMAT_YEAR_MONTH, DATE_FORMAT_2 } from "constant/date";
import moment from "moment";
import dayjs from "dayjs";

type HeaderColumn = {
  label: string;
  key: string;
  style?: React.CSSProperties;
  styleRow?: React.CSSProperties;
  render?: (value: any, row: any) => React.ReactNode;
  groups?: {
    label: string;
    key: string;
    style?: React.CSSProperties;
    styleRow?: React.CSSProperties;
  }[];
};
type RowData = {
  homeCareSupportOfficeNameInsuranceCard?: string | null;
  businessNumberProfile?: string | null;
  serviceNameServiceDetail?: string;
  subtotalServiceDetail?: number;
  categoryLimitWithinLimitUnits?: number;
  unitPrice?: number;
  totalCostCovered?: string;
  benefitLimitInsuranceCard?: number;
  insuranceClaimAmount?: string;
  userCopaymentCovered?: string;
};

interface IProps {
  nurseCareLevel?: ICareCategorySetting | null;
  itemService: IItemService;
  servicesDetailRows: RowData[];
  totalServicesDetailRow: RowData;
  treatmentDataRows: RowData[];
  currentDate?: dayjs.Dayjs;
}

const MIN_NUMBER_ROW_EMPTY = 14;
const UserBurdenCalculationTable = ({
  nurseCareLevel,
  itemService,
  servicesDetailRows,
  totalServicesDetailRow,
  treatmentDataRows,
  currentDate,
}: IProps) => {
  const headerColumns = [
    {
      //3
      label: "事業所名",
      key: "homeCareSupportOfficeNameInsuranceCard",
      style: {
        width: "24mm",
      },
      styleRow: {
        lineHeight: "12px",
        paddingTop: "1px",
        paddingLeft: "1px",
      },
    },
    {
      //4
      label: "事業所番号",
      key: "businessNumberProfile",
      styleRow: {
        lineHeight: "12px",
        paddingTop: "1px",
        paddingLeft: "1px",
      },
    },
    {
      //5
      label: "サービス内容\n/種類",
      key: "serviceNameServiceDetail",
      style: {
        width: "27mm",
      },
      styleRow: {
        lineHeight: "12px",
        paddingTop: "1px",
        paddingLeft: "1px",
        fontSize: "9px",
      },
    },
    {
      //6
      label: "サービス\nコード",
      key: "serviceCodeManualServiceDetail",
      style: {
        width: "12mm",
      },
      styleRow: {
        lineHeight: "12px",
        paddingLeft: "1px",
        fontSize: 11,
      },
    },
    {
      //7
      label: "単位数",
      key: "unitPriceServiceDetail",
      styleRow: {
        fontSize: 11,
        textAlign: "right",
      },
      style: {
        width: "9mm",
      },
    },
    {
      label: "割引適用後",
      key: "afterDiscount",
      style: { width: "17mm" },
      groups: [
        {
          label: "率(%)",
          key: "discountRate",
          style: { maxWidth: "7mm" },
          styleRow: {
            textAlign: "right",
          },
        },
        {
          label: "単位数",
          key: "discountedUnitCount",
          style: { maxWidth: "10mm" },
          styleRow: {
            textAlign: "right",
          },
        },
      ],
    },
    {
      //9
      label: "回数",
      key: "quantityServiceDetail",
      style: {
        width: "7mm",
      },
      styleRow: {
        textAlign: "right",
      },
    },
    {
      //10
      label: "サービス\n単位数\n/金額",
      key: "subtotalServiceDetail",
      style: {
        width: "13mm",
      },
      styleRow: {
        textAlign: "right",
      },
    },
    {
      label: "給付管理\n単位数",
      key: "benefitManagementUnits",
      style: {
        width: "12mm",
      },
      styleRow: {
        textAlign: "right",
      },
    },
    {
      label: "種類支給限度\n基準を超える\n単位数",
      key: "typeLimitExceededUnits",
      style: {
        width: "15mm",
      },
      styleRow: {
        textAlign: "right",
      },
    },
    {
      label: "種類支給限\n度基準内単\n位数",
      key: "typeLimitWithinLimitUnits",
      style: {
        width: "14mm",
      },
      styleRow: {
        textAlign: "right",
      },
    },
    {
      label: "区分支給限\n度基準を超え\nる単位数",
      key: "categoryLimitExceededUnits",
      style: {
        width: "15mm",
      },
      styleRow: {
        textAlign: "right",
      },
    },
    {
      //12
      label: "区分支給限\n度基準内単\n位数",
      key: "categoryLimitWithinLimitUnits",
      style: {
        width: "13mm",
      },
      styleRow: {
        textAlign: "right",
      },
    },
    {
      //13
      label: "単位数\n単価",
      key: "unitPrice",
      style: {
        width: "8mm",
      },
      styleRow: {
        textAlign: "right",
      },
    },
    {
      //14
      label: "費用総額\n(保険/事業\n対象分)",
      key: "totalCostCovered",
      style: {
        width: "13mm",
      },
      styleRow: {
        textAlign: "right",
      },
    },
    {
      //15
      label: "給付率\n(%)",
      key: "benefitLimitInsuranceCard",
      style: {
        width: "8mm",
      },
      styleRow: {
        textAlign: "right",
      },
    },
    {
      //16
      label: "保険/事業費\n請求額",
      key: "insuranceClaimAmount",
      style: {
        width: "14mm",
      },
      styleRow: {
        textAlign: "right",
      },
    },
    {
      //17
      label: "定額\n利用者負担\n単価金額",
      key: "fixedUserCopayment",
      style: {
        width: "13mm",
      },
      styleRow: {
        textAlign: "right",
      },
    },
    {
      //18
      label: "利用者負担\n(保険/事業\n対象分)",
      key: "userCopaymentCovered",
      style: {
        width: "13mm",
      },
      styleRow: {
        textAlign: "right",
      },
    },
    {
      //19
      label: "利用者負担\n(全額負担分)",
      key: "userFullPayment",
      style: {
        width: "15mm",
      },
      styleRow: {
        textAlign: "right",
      },
    },
  ] as HeaderColumn[];

  const { insurance_card, client } = itemService;

  const data = [
    ...servicesDetailRows,
    servicesDetailRows.length > 0 ? totalServicesDetailRow : null,
    ...treatmentDataRows,
  ].filter((x) => !!x) as any[];

  const numberRestEmptyRow =
    MIN_NUMBER_ROW_EMPTY - data.length > 0
      ? MIN_NUMBER_ROW_EMPTY - data.length
      : 0;

  const rows = [
    ...data,
    ...Array.from({ length: numberRestEmptyRow }, () => ({})),
  ];

  const monthSelected = moment(dayjs(currentDate).format(DATE_FORMAT_2)).format(
    DATE_ERA_FORMAT_YEAR_MONTH
  );

  const renderTableHeader = (headerColumns: HeaderColumn[]) => {
    return (
      <thead>
        <tr>
          {headerColumns.map((column) => {
            const hasGroups = column.groups?.length;

            if (hasGroups) {
              return (
                <th
                  key={column.key}
                  colSpan={column.groups!.length}
                  rowSpan={1}
                  style={{ ...column.style }}
                  className="p-0 text-center whitespace-pre-line text-[9px] font-normal"
                >
                  {column.label}
                </th>
              );
            }

            return (
              <th
                key={column.key}
                rowSpan={2}
                style={{ ...column.style }}
                className="p-0 py-1 text-center whitespace-pre-line text-[9px] font-normal leading-[1.1]"
              >
                {column.label}
              </th>
            );
          })}
        </tr>

        {/* ROW 2 */}
        <tr>
          {headerColumns.flatMap(
            (column) =>
              column.groups?.map((group) => (
                <th
                  key={`${column.key}-${group.key}`}
                  style={{ ...group.style }}
                  className="p-0 text-center whitespace-pre-line text-[9px] font-normal"
                >
                  {group.label}
                </th>
              )) || []
          )}
        </tr>
      </thead>
    );
  };

  const renderLastRow = () => {
    const totalCostCoveredRows = new Decimal(
      totalServicesDetailRow.totalCostCovered || 0
    )
      .plus(
        treatmentDataRows.reduce((sum, row) => {
          return sum.plus(new Decimal(row.totalCostCovered || 0));
        }, new Decimal(0))
      )
      .toNumber();

    const insuranceClaimAmountRows = new Decimal(
      totalServicesDetailRow.insuranceClaimAmount || 0
    )
      .plus(
        treatmentDataRows.reduce((sum, row) => {
          return sum.plus(new Decimal(row.insuranceClaimAmount || 0));
        }, new Decimal(0))
      )
      .toNumber();

    const userCopaymentCoveredRows = new Decimal(
      totalServicesDetailRow.userCopaymentCovered || 0
    )
      .plus(
        treatmentDataRows.reduce((sum, row) => {
          return sum.plus(new Decimal(row.userCopaymentCovered || 0));
        }, new Decimal(0))
      )
      .toNumber();

    return (
      <tr>
        <DiagonalLine />
        <td colSpan={3} className="text-center font-bold text-sm leading-[1.1]">
          区分支給限度基準額（単位）
        </td>
        <td colSpan={3} className="text-xl text-right leading-[1.1]">
          {nurseCareLevel?.unit}
        </td>
        <td className="font-bold text-[11px] text-center">合計</td>
        <td className="text-right text-[11px]">
          {totalServicesDetailRow.subtotalServiceDetail}
        </td>
        <td className="text-right text-[11px]"></td>
        <td className="text-right text-[11px]"></td>
        <td className="text-right text-[11px]"></td>
        <td className="text-right text-[11px]">0</td>
        <td className="text-right text-[11px]">
          {totalServicesDetailRow.subtotalServiceDetail}
        </td>
        <DiagonalLine />
        <td className="text-right text-[11px]"> {totalCostCoveredRows}</td>
        <DiagonalLine />
        <td className="text-right text-[11px]"> {insuranceClaimAmountRows}</td>
        <DiagonalLine />
        <td className="text-right text-[11px]"> {userCopaymentCoveredRows}</td>
        <td className="text-right text-[11px]">0</td>
      </tr>
    );
  };

  const renderTableBody = () => {
    return (
      <tbody>
        {rows.map((row, index) => (
          <tr key={index}>
            {headerColumns.map((column) => {
              const hasGroups = column.groups?.length;

              if (hasGroups) {
                return column.groups!.map((group) => (
                  <td
                    key={`${column.key}-${group.key}`}
                    className="p-0 "
                    style={{ fontSize: 10, minHeight: "5mm" }}
                  >
                    <div
                      className="min-h-[7mm]"
                      style={{ ...group.style, ...group.styleRow }}
                    >
                      {row[group.key]}
                    </div>
                  </td>
                ));
              }

              return (
                <td
                  key={column.key}
                  className="p-0 align-top"
                  style={{
                    fontSize: 10,
                    lineHeight: "1.2",
                    ...column.style,
                    ...column.styleRow,
                  }}
                >
                  <div
                    className="min-h-[7mm]"
                    style={{
                      ...column.style,
                      ...column.styleRow,
                      paddingRight: "2px",
                    }}
                  >
                    {column.render
                      ? column.render(row[column.key], row)
                      : row[column.key]}
                  </div>
                </td>
              );
            })}
          </tr>
        ))}

        {renderLastRow()}
      </tbody>
    );
  };

  return (
    <div>
      <div>
        <div className="flex justify-between items-end">
          <div className="flex-1" />
          <div className="flex-1 font-bold text-lg">
            {monthSelected} サービス提供票別表
          </div>
          <div className="">
            <div className="text-xs border h-[5mm] px-2 flex items-center justify-center border-black">
              サービス事業所→居宅介護支援事業所
            </div>
          </div>
        </div>
        <div className="flex text-xs justify-between">
          <div className="font-bold flex-1 ml-10">
            区分支給限度管理・利用者負担計算
          </div>
          <div className="flex justify-between flex-1 text-sm h-[18px]">
            <div className="border-b border-black leading-normal ">
              被保険者番号 : {insurance_card?.insurance_number}
            </div>
            <div className="border-b border-black min-w-[75mm] flex justify-between leading-normal ">
              <span>利用者 : {client.family_name}</span>{" "}
              <span>様</span>
            </div>
          </div>
        </div>
      </div>
      <table className="w-full mt-[0.5mm] table-fixed">
        {renderTableHeader(headerColumns)}
        {renderTableBody()}
      </table>
    </div>
  );
};
export default UserBurdenCalculationTable;
